<template>
  <div id="accordionTourComplementary" class="accordion">
    <div class="case-details accordion-item">
      <h2 id="headingTourComplementary" class="accordion-header">
        <button
          aria-controls="collapseTourComplementary"
          aria-expanded="true"
          class="accordion-button collapsed"
          data-bs-target="#collapseTourComplementary"
          data-bs-toggle="collapse"
          type="button"
        >
          <div class="mb-0 d-flex align-items-center">
            <div class="bg-primary rounded-xs me-8 d-flex align-items-center justify-content-center" style="width: 24px; height: 24px; flex: none;">
              <v-icon icon="contact" size="xs" class="text-white" />
            </div>

            <div>
              <div class="h6 ff-secondary mb-0 fw-bold">{{ $t("clientTour.complementaryText") }}</div>
              <div class="fw-light accordion-subtitle text-xs" style="font-size: 10px !important;">{{ $t("clientTour.complementarySubtitle") }}</div>
            </div>
          </div>
        </button>
      </h2>

      <div
        id="collapseTourComplementary"
        aria-labelledby="headingTourComplementary"
        class="accordion-collapse collapse show"
        data-bs-parent="#accordionTourComplementary"
      >
        <div class="accordion-body pb-16 pb-xl-24">
          <form id="case-tour-update-complementary-text-form" class="d-flex flex-column justify-content-between h-100">
            <fieldset>
              <legend class="visually-hidden">{{ $t("clientTour.complementaryConditions") }}</legend>

              <div class="form-outline">
                <label class="label text-xs fw-bold text-uppercase text-muted mb-2">
                  <span>{{ $t("clientTour.complementaryText") }}</span>
                </label>

                <v-html-editor
                  id="case-tour-update-complementary-text"
                  ref="case-tour-update-complementary-text-ref"
                  :value="complementaryLocal"
                  @input="complementaryLocal = $event"
                  @media-uploaded="editorMedia.push($event)"
                  height="300"
                />
              </div>
            </fieldset>

            <v-button
              :disabled="!meta.valid || disableButton || !meta.dirty"
              :is-loading="accepted"
              class="btn-icon w-fit align-self-end mt-12"
              size="xs"
              variant="primary"
              @click="updateTour"
            >
              {{ $t("general.button.save") }}
              <v-icon icon="arrow-right" size="xs" space="ms-8" />
            </v-button>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import * as yup from "yup";
import { useField } from "vee-validate";
import VIcon from "@/components/vendor/basic/icon/VIcon.vue";
import VHtmlEditor from "@/components/vendor/htmlEditor/VHtmlEditor.vue";
import api from "@/api";
import useFormValidation from "@/helpers/form";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import ClientTour from "@/api/objects/ClientTour";
import { useToast } from "vue-toastification";

const props = defineProps<{
  agency: string;
  clientTour: ClientTour;
  caseId: number;
}>();

const emit = defineEmits<{
  (e: "reloadCase"): void;
}>();

const { t } = useI18n();
const toast = useToast();

const rules = yup.object({
  complementaryLocal: yup.string().nullable().label(t("clientTour.complementaryText")),
});

const initialValues = {
  complementaryLocal: props.clientTour.extra_text,
};

const form = useFormValidation(rules, initialValues);
const { accepted, meta, toggleAccepted } = form;

const { value: complementaryLocal } = useField<string>("complementaryLocal");

const disableButton = ref(false);
const editorMedia = ref<any[]>([]);

const updateTour = async () => {
  try {
    toggleAccepted();
    disableButton.value = true;

    const data = {
      type: "extra_text",
      text: complementaryLocal.value,
    };

    await api.case.updateContactInfo(props.agency, props.caseId, data);

    if (editorMedia.value?.length > 0) {
      const mediaPayload = {
        media: editorMedia.value,
        model_type: "client_tour",
        collection: "tinymce",
        model_id: props.clientTour.id,
      };

      await api.media.confirm("s3", mediaPayload);
    }

    toast.success(t("general.shared.savedChanges"));
    emit("reloadCase");
  } catch (e: any) {
    console.error(e);
    toast.error(e?.response?.data?.message || t("general.shared.somethingWentWrong"));
  } finally {
    disableButton.value = false;
    toggleAccepted();
  }
};
</script>

<style lang="scss" scoped>
.accordion-button,
.accordion-button:not(.collapsed) {
  background-color: transparent;
  padding: 0.5rem 1rem !important;
}

:deep(.form-control),
:deep(.vs__selected),
:deep(.vs__dropdown-option) {
  font-size: 0.8rem !important;
}

:deep(.v-form-label),
:deep(.form-check-label) {
  font-size: 0.75rem !important;
}

:deep(.v-form-input),
:deep(.v-form) {
  margin-bottom: 0.25rem !important;
}
</style>
